"use client";

import NewsletterInput from "@/components/atoms/NewsletterInput";
import Preamble from "@/components/atoms/Preamble/Preamble";
import getLink from "@/lib/storyblok/utils/getLink";
import {
  FooterSectionStoryblok,
  NewsletterSignupStoryblok,
} from "@/types/storyblok-blok-types";
import clsx from "clsx";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { NewsLetterPopup } from "../NewsletterPopup/NewsletterPopup";
import { ResolveFooterDataStoryblok } from "./Footer";
import styles from "./footer.module.css";
import Marquee from "./Marquee";

export type MarqueeData = ResolveFooterDataStoryblok["marquee"];

type FlavourText = ResolveFooterDataStoryblok["newsletterFlavourText"];
type SocialLink = {
  uri: string;
  name: string;
  icon: React.JSX.Element;
};

const RegularSection = ({ section }: { section: FooterSectionStoryblok }) => (
  <div className={styles.col}>
    {section?.name && (
      <Preamble className={styles.preamble}>{section?.name}</Preamble>
    )}
    <nav className={styles.links}>
      {section?.links?.map((linkData, i) => {
        const text = linkData.text;
        const link = getLink(linkData.link);

        return (
          <Link
            href={link ?? "#"}
            data-link={link}
            key={i}
            className="underline-hover"
          >
            {text}
          </Link>
        );
      })}
    </nav>
  </div>
);

const Credentials = () => (
  <div className={styles.credentials}>
    <p className="uppercase">{"©2026 Camilla Pihl"}</p>
    <p className="uppercase">{"Fashion Group"}</p>
    <p className="uppercase">
      {"Developed by "}
      <Link
        href="https://www.try.no/"
        className={clsx(styles["company-link"], "underlined")}
      >
        TRY DIG
      </Link>
    </p>
    <p className="uppercase">
      {"Designed by "}
      <Link
        href="https://bleed.com/"
        className={clsx(styles["company-link"], "underlined")}
      >
        BLEED
      </Link>
    </p>
  </div>
);

const NewsletterSection = ({ flavourText }: { flavourText: FlavourText }) => (
  <div className={styles.newsletter}>
    <Preamble className={styles.preamble}>{"STAY UP TO DATE"}</Preamble>
    <div className={styles.links}>
      <NewsletterInput />
      <p className={styles.flavour__text}>{flavourText}</p>
    </div>
  </div>
);

const Socials = ({ links }: { links: SocialLink[] }) => {
  return (
    <nav className={styles.socials}>
      {links?.map((link, i) => (
        <Link
          href={link.uri || "#"}
          className={clsx(styles.social__link, "underline-hover")}
          key={i}
        >
          {link.icon}
          <span className="sr-only">{link.name}</span>
        </Link>
      ))}
    </nav>
  );
};

export const FooterUI = ({
  sections,
  marquee,
  newsletterFlavourText,
  socialLinks,
  newsletterPopup,
}: {
  sections?: FooterSectionStoryblok[];
  newsletterFlavourText: string;
  socialLinks: SocialLink[];
  marquee?: string[];
  newsletterPopup?: NewsletterSignupStoryblok;
}) => {
  const pathname = usePathname();
  const isCheckout =
    pathname.includes("checkout") && !pathname.includes("summary");

  if (isCheckout) return null;
  return (
    <footer className={styles.root}>
      <Marquee data={marquee} />
      <section id="footer" className={styles.footer}>
        <div className={styles.grid}>
          {sections &&
            [sections?.[0], sections?.[1], sections?.[2], sections?.[3]]?.map(
              (section, sectionIdx) => (
                <RegularSection key={sectionIdx} section={section} />
              ),
            )}
          <NewsletterSection flavourText={newsletterFlavourText} />
          {newsletterPopup && <NewsLetterPopup blok={newsletterPopup} />}
        </div>
        <div className={clsx(styles.grid, styles.socials__grid)}>
          <Socials links={socialLinks ?? []} />
          <Credentials />
        </div>
      </section>
    </footer>
  );
};
